import {
    MILITARY_VARIANT,
    STC_VARIANT,
    openFormsSignatory,
    readCsrCount,
    revisionIdPath,
    revisionNumberPath,
    revisionRefsPath,
    revisionSnapshotsPath,
    signatoriesPath,
    signatoryPath,
    testerPath,
    testersPath
} from "../_variants/index.js";
import { makeAttached, next } from "../../../utils";

import { appConfig } from "../../../db.js";
import changeIdentifier from "./changeIdentifier";
import { findSignatoryFile } from "./signatoryIdentity.common.js";
import {
    get
} from "lodash";

async function getRequiredData(db, store) {
    await db.watch("Matrices/SignatoryMatrix/Panels", store);
    await db.watch("Matrices/SignatoryMatrix/Types", store);

    const panels = Object.values(store.state["Matrices/SignatoryMatrix/Panels"]).map(panel => {
        return {
            "type": "confirmationInput",
            "label": `${panel.NamePretty} - Limitations`,
            "name": `Panels[${panel.ID}][Answer]`,
            "inputName": `Panels[${panel.ID}][Limitations]`,
            "inputValue": true
        }
    });

    const types = Object.values(store.state["Matrices/SignatoryMatrix/Types"]).map(type => {
        return {
            "label": type.Type,
            "id": type.Type
        }
    });

    return {
        panels,
        types
    }
}

const personalDataFields = [{
    "label": "First Name",
    "name": "FirstName",
    "type": "text"
}, {
    "label": "Last Name",
    "name": "LastName",
    "type": "text"
}, {
    "label": "Email",
    "name": "Email",
    "type": "text"
}, {
    "label": "Position",
    "name": "Position",
    "type": "text"
}];

async function getPanelFields(db, store) {
    const {
        panels
    } = await getRequiredData(db, store);

    return [{
        "label": "Discipline: Areas of Expertise",
        "divClass": "col-span-3",
        "type": "sectionHeader"
    }, ...panels];
}

const aircraftFields = [{
    "label": "Aeroplane Types",
    "divClass": "col-span-3",
    "type": "sectionHeader"
}, {
    "label": "CS-23",
    "name": "CS23",
    "type": "toggle",
    "required": false
}, {
    "label": "CS-25",
    "name": "CS25",
    "type": "toggle",
    "required": false
}, {
    "label": "CS-27",
    "name": "CS27",
    "type": "toggle",
    "required": false
}, {
    "label": "CS-29",
    "name": "CS29",
    "type": "toggle",
    "required": false
}];

const activityFields = [{
    "label": "CVE",
    "divClass": "col-span-3",
    "type": "sectionHeader"
}, {
    "label": "CVE",
    "name": "CVE",
    "type": "toggle",
    "required": false
}, {
    "label": "Activity",
    "divClass": "col-span-3",
    "type": "sectionHeader"
}, {
    "label": "Test Witness",
    "name": "TestWitness",
    "type": "toggle",
    "required": false
}, {
    "label": "ICA",
    "name": "ICA",
    "type": "toggle",
    "required": false
}];

const basePanelValues = (store) => {
    return Object
        .values(store.state["Matrices/SignatoryMatrix/Panels"])
        .reduce((obj, panel) => {
            obj[panel.ID] = {};
            obj[panel.ID].Limitations = "";
            obj[panel.ID].Answer = false;
            obj[panel.ID].ID = panel.ID;
            return obj;
        }, {});
};

export const addEmployee = async ({ db, store, form, meta }) => {
    const variant = meta?.formMetadata?.ParentType === "MilitarySignatoryMatrix" || meta?.ParentType === "MilitarySignatoryMatrix"
        ? MILITARY_VARIANT
        : STC_VARIANT;

    const fs = [{
        "label": "TSR Number",
        "name": "CSR",
        "type": "value",
        value(_, store) {
            return readCsrCount(store, variant);
        }
    }, ...personalDataFields.map(f => {
        // AccountID is no longer required - callable accepts email-only for brand-new people
        if (f.name === "AccountID") {
            return { ...f, required: false, label: "Link to Account (optional)" };
        }
        return f;
    })];

    // Track previous AccountID in closure for change detection
    let prevAccountID = undefined;

    const formTitle = variant.key === "military" ? "Add New Military Signatory" : "Add New Signatory";
    form.setConfig({
        "title": formTitle,
        "submitButtonLabel": "Save",
        "type": "ignoreAutoAction",
        "fields": fs,
        // Dynamic form handler: auto-fill email when AccountID is selected
        onFormChanged(formData) {
            const currentAccountID = formData.AccountID;
            
            if (currentAccountID !== prevAccountID) {
                if (currentAccountID) {
                    // Mode A: User selected an existing account - auto-fill email from that user
                    const users = store.state.users || {};
                    const selectedUser = users[currentAccountID];
                    
                    if (selectedUser) {
                        const userEmail = selectedUser.email || selectedUser.Email;
                        if (userEmail) {
                            // Auto-fill email from the selected user
                            form.setValue("Email", userEmail);
                            
                            // Optionally auto-fill names if empty
                            if (!formData.FirstName && selectedUser.name) {
                                const nameParts = (selectedUser.name || "").split(" ");
                                if (nameParts.length > 0) {
                                    form.setValue("FirstName", nameParts[0]);
                                }
                                if (nameParts.length > 1) {
                                    form.setValue("LastName", nameParts.slice(1).join(" "));
                                }
                            }
                        }
                    }
                } else {
                    // AccountID was cleared - clear email to avoid stale auto-filled value
                    form.setValue("Email", "");
                }
                
                prevAccountID = currentAccountID;
            }
        },
        async preSubmitHook({ data }) {
            // Validate required fields
            if (!data.Email) throw new Error("Email is required");
            if (!data.FirstName) throw new Error("First Name is required");
            if (!data.LastName) throw new Error("Last Name is required");

            // Validate no mismatch between AccountID and Email (Mode A/B distinction)
            if (data.AccountID) {
                // Mode A: Existing user selected - verify email matches
                const users = store.state.users || {};
                const selectedUser = users[data.AccountID];
                
                if (!selectedUser) {
                    throw new Error(`Selected account (${data.AccountID}) not found in users list`);
                }
                
                const userEmail = selectedUser.email || selectedUser.Email;
                if (userEmail && userEmail.toLowerCase() !== data.Email.toLowerCase()) {
                    throw new Error(
                        `Email mismatch: The selected account belongs to ${userEmail}, ` +
                        `but you entered ${data.Email}. Please either select the correct account or ` +
                        `clear the "Link to Account" field to create a new person.`
                    );
                }
            }
            // Mode B: No AccountID provided - brand-new person, email will be used to create Auth

            // Import callable and appConfig dynamically
            const { callable, appConfig } = await import("../../../db.js");
            
            // Call the new callable to provision the signatory person atomically
            const response = await callable("provisionSignatoryPerson")({
                databaseURL: appConfig.firebaseConfig.databaseURL,
                variant: variant.key,
                email: data.Email,
                firstName: data.FirstName,
                lastName: data.LastName,
                position: data.Position || "",
                uid: data.AccountID || undefined,
            });

            // Unwrap .data from httpsCallable ansew
            const result = response.data;

            // Return the result for display
            return {
                uid: result.uid,
                created: result.created,
                matrixWritten: result.matrixWritten,
                csr: result.csr,
            };
        },
        async postSubmitHook({ newData, db }) {
            // No-op - callable handles all writes atomically
            // Optionally refresh the list view
            console.log(`Signatory provisioned: uid=${newData.uid}, created=${newData.created}, CSR=${newData.csr}`);
        }
    });
    form.getDropdownData();
    form.toggle();
};

const panelsToValues = (store, pageItem) => {
    let acc = []
    Object
        .values(store.state["Matrices/SignatoryMatrix/Panels"])
        .forEach((panel) => {
            acc.push([`Panels[${panel.ID}][Limitations]`, get(pageItem, `Panels.${panel.ID}.Limitations`, '')]);
            acc.push([`Panels[${panel.ID}][Answer]`, get(pageItem, `Panels.${panel.ID}.Answer`, "Compiler")]);
        })
        
    return Object.fromEntries(acc);
};

export const updateEmployee = async ({ db, item, store, form, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;

    form.setValues({
        ...item
    });

    form.setConfig({
        "title": "Update Employee",
        "submitButtonLabel": "Save",
        "type": "update",
        "location": signatoryPath(variant, item.ID),
        // AccountID is not client-writable (database rules); linking happens via the add flow/callable.
        "fields": personalDataFields.filter(field => field.name !== "AccountID"),
        postSubmitHook({ db }) {
            db.update(signatoryPath(variant, item.ID), {
                UserSignOff: false,
                UserSignOffDate: null,
                UserSignOffBy: null
            });
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const updatePanels = async ({ form, pageItem, store, db, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    const fields = await getPanelFields(db, store);

    form.setValues({
        ...pageItem,
        ...panelsToValues(store, pageItem)
    });

    console.log(signatoryPath(variant, pageItem.ID))

    form.setConfig({
        "title": "Update Panels",
        "type": "update",
        "location": signatoryPath(variant, pageItem.ID),
        "fields": fields
    });

    form.toggle();
};

export const updateApprovedFunctions = ({ form, pageItem, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    const totalPoints = Object.values(pageItem.InterviewQuestions || {}).reduce((acc, q) => {
        return acc + parseFloat(q.Points || 0);
    }, 0);
    form.setValues({
        "ApprovedFunctions": Object.keys(pageItem.ApprovedFunctions || {})
    });
    form.setConfig({
        "title": "Update Approved Functions",
        "type": "update",
        "set": true,
        "location": signatoryPath(variant, pageItem.ID),
        "fields": [{
            "label": "Approved Functions",
            "name": "ApprovedFunctions",
            "type": "dropdown",
            "dataLocation": "Matrices/SignatoryMatrix/ApprovedFunctions",
            "labelKey": "Name",
            "idKey": "ID",
            "multiple": true,
            "divClass": "md:col-span-3"
        }],
        preSubmitHook({ data }) {
            return {
                "ApprovedFunctions": makeAttached(data.ApprovedFunctions)
            }
        },
        postSubmitHook({ db }) {
            db.update(signatoryPath(variant, pageItem.ID), {
                UserSignOff: false,
                UserSignOffDate: null,
                UserSignOffBy: null
            });
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const bulkApprove = ({ form, selectedPeople, store, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    const panels = Object.values(store.state["Matrices/SignatoryMatrix/Panels"] || {})
        .map(panel => ({
            "label": panel.NamePretty || panel.Name,
            "id": panel.ID
        }));
    const panelField = (label, name) => ({
        label,
        name,
        "type": "dropdown",
        "dropdownData": panels,
        "multiple": true,
        "showSelectAll": true,
        "search": true,
        "required": false,
        "divClass": "md:col-span-3"
    });
    const selectedIds = selectedPeople.map(person => person.ID).filter(Boolean);
    const asArray = value => Array.isArray(value) ? value : value ? [value] : [];

    form.setValues({
        "ApprovedFunctions": [],
        "CompilerPanels": [],
        "CheckerPanels": [],
        "ApproverPanels": [],
        "CVEPanels": []
    });
    form.setConfig({
        "title": `Bulk Approve ${selectedIds.length} Signator${selectedIds.length === 1 ? "y" : "ies"}`,
        "description": "Selected approvals will be added to each person. Where a panel is selected more than once, CVE takes precedence over Approver, Checker, then Compiler.",
        "submitButtonLabel": "Apply Approvals",
        "successMessage": `Approvals applied to ${selectedIds.length} signator${selectedIds.length === 1 ? "y" : "ies"}`,
        "type": "ignoreAutoAction",
        "fields": [{
            "label": "Approved Functions",
            "name": "ApprovedFunctions",
            "type": "dropdown",
            "dataLocation": "Matrices/SignatoryMatrix/ApprovedFunctions",
            "labelKey": "Name",
            "idKey": "ID",
            "multiple": true,
            "showSelectAll": true,
            "search": true,
            "required": false,
            "divClass": "md:col-span-3"
        },
        panelField("Compiler Panels", "CompilerPanels"),
        panelField("Checker Panels", "CheckerPanels"),
        panelField("Approver Panels", "ApproverPanels"),
        panelField("CVE Panels", "CVEPanels")],
        async preSubmitHook({ data, db }) {
            const approvedFunctions = asArray(data.ApprovedFunctions);
            const roleSelections = [
                ["Compiler", asArray(data.CompilerPanels)],
                ["Checker", asArray(data.CheckerPanels)],
                ["Approver", asArray(data.ApproverPanels)],
                ["CVE", asArray(data.CVEPanels)]
            ];

            if (!approvedFunctions.length && !roleSelections.some(([, panelIds]) => panelIds.length)) {
                throw new Error("Select at least one approved function or panel role.");
            }

            const panelRoles = new Map();
            roleSelections.forEach(([role, panelIds]) => {
                panelIds.forEach(panelId => panelRoles.set(panelId, role));
            });

            const updates = {};
            selectedIds.forEach(personId => {
                approvedFunctions.forEach(functionId => {
                    updates[`${personId}/ApprovedFunctions/${functionId}`] = true;
                });
                panelRoles.forEach((role, panelId) => {
                    updates[`${personId}/Panels/${panelId}/ID`] = panelId;
                    updates[`${personId}/Panels/${panelId}/Answer`] = role;
                });
                updates[`${personId}/UserSignOff`] = false;
                updates[`${personId}/UserSignOffDate`] = null;
                updates[`${personId}/UserSignOffBy`] = null;
            });

            await db.update(signatoriesPath(variant), updates);
            return {};
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const updatePanel = async ({ form, rowItem, pageItem, store, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    
    console.log(signatoryPath(variant, pageItem.ID), meta, variant);
    let af = store.state["Matrices/SignatoryMatrix/ApprovedFunctions"];

    const approvedFunctions = Object.keys(pageItem.ApprovedFunctions || {}).map(key => {
        return af[key];
    });

    const options = ([{
        "label": "Compiler",
        "id": "Compiler"
    }, {
        "label": "Checker",
        "id": "Checker"
    }, {
        "label": "Approver",
        "id": "Approver"
    }, {
        "label": "CVE",
        "id": "CVE"
    }]).filter(i => {
        let x = approvedFunctions.find(a => {

            return a?.Name === i?.label
        });
        return x;
    });

    form.setValues(rowItem);
    console.log(signatoryPath(variant, pageItem.ID));
    form.setConfig({
        "title": "Update Panels",
        "type": "update",
        "location": `${signatoryPath(variant, pageItem.ID)}/Panels/${rowItem.ID}`,
        "fields": [{
            "name": "Answer",
            "label": "Checker / Approver / CVE",
            "type": "dropdown",
            "dropdownData": [{
                "label": "None",
                "id": "None"
            }, ...options]
        }],
        postSubmitHook({ db }) {
            db.update(signatoryPath(variant, pageItem.ID), {
                UserSignOff: false,
                UserSignOffDate: null,
                UserSignOffBy: null
            });
        }
    });

    form.getDropdownData();
    form.toggle();
};

export const addPanelLimitations = async ({ form, rowItem, pageItem, store, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    form.setValues(rowItem);
    form.setConfig({
        "title": "Add Panel Limitation",
        "type": "push",
        "location": `${signatoryPath(variant, pageItem.ID)}/Panels/${rowItem.ID}/Limitations`,
        "fields": [{
            "label": "Limitation",
            "name": "Limitation",
            "type": "text",
            "divClass": "md:col-span-3"
        }],
        postSubmitHook({ db }) {
            db.update(signatoryPath(variant, pageItem.ID), {
                UserSignOff: false,
                UserSignOffDate: null,
                UserSignOffBy: null
            });
        }
    });
    form.toggle();
};

export const updatePanelLimitations = ({ form, rowItem, pageItem, store, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;

    const fields = Object.values(rowItem.Limitations || {}).map((limitation, index) => {
        return {
            "label": "Limitation (Leave blank to delete)",
            "name": `Limitations[${limitation.ID}]`,
            "type": "text",
            "required": false,
            "divClass": "md:col-span-3",
            "value": limitation.Limitation
        };
    });

    form.setValues(Object.fromEntries(fields.map(f => [f.name, f.value])));

    form.setConfig({
        "title": "Update Panel Limitations",
        "type": "set",
        "description": "Leave blank to delete",
        "set": true,
        "location": `${signatoryPath(variant, pageItem.ID)}/Panels/${rowItem.ID}/Limitations`,
        "fields": fields,
        preSubmitHook({ data }) {
            let entries = Object.entries(data.Limitations || {}).filter(([key, value]) => {
                return value.Limitation !== "";
            }).map(([key, value]) => {
                return [key, {
                    "ID": key,
                    "Limitation": value
                }];
            });

            return {
                "Limitations": null,
                ...(Object.fromEntries(entries))
            };
        },
        postSubmitHook({ db }) {
            db.update(signatoryPath(variant, pageItem.ID), {
                UserSignOff: false,
                UserSignOffDate: null,
                UserSignOffBy: null
            });
        }
    });

    form.toggle();
};

export const updatePanelLimitations2 = ({ form, rowItem, pageItem, store, meta }) => () => {
    updatePanelLimitations({ form, rowItem, pageItem, store, meta });
};

export const uploadFile = ({ form, pageItem, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    form.setConfig({
        "title": "Add File",
        "type": "push",
        "location": `${signatoryPath(variant, pageItem.ID)}/Files`,
        "fields": [{
            "label": "File",
            "name": "File",
            "type": "file",
            "accept": ".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
        }],
        async postSubmitHook({ db, typeRef, files }) {
            for (const file of files) {
                await db.set(`${signatoryPath(variant, pageItem.ID)}/Files/${typeRef.key}/File`, file[0].name);
                await db.upload(`people/${pageItem.ID}`, file[0]);
            }
        }
    });
    form.toggle();
};

export async function downloadFile({ db, rowItem, pageItem, toast }) {
    let url = null;
    try {
        url = await findSignatoryFile(db, pageItem, rowItem.File);
    } catch {
        toast.error("Unable to download file");
        return;
    }

    if (url) {
        window.open(url, "_blank");
    } else {
        toast.error("File not found");
    }
};

export const downloadFile2 = async ({ rowItem, pageItem, db, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    const url = await db.download(`${openFormsSignatory(variant, pageItem.ID)}/${rowItem.File}`);
    window.open(url, "_blank");
};

export const deleteFile = ({ form, rowItem, pageItem, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    form.setConfig({
        "title": "Delete File",
        "type": "delete",
        "location": `${signatoryPath(variant, pageItem.ID)}/Files/${rowItem.ID}`,
        "fields": [],
        async postSubmitHook({ db }) {
            await db.remove(`people/${pageItem.ID}`, rowItem.File);
        }
    });
    form.toggle();
};

export const makeInactive = ({ form, pageItem, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    form.setConfig({
        "title": "Make Signatory Inactive",
        "type": "update",
        "location": signatoryPath(variant, pageItem.ID),
        "fields": [],
        preSubmitHook() {
            return {
                "Cancelled": true,
                "UserSignOff": false,
                "UserSignOffDate": null,
                "UserSignOffBy": null
            }
        }
    });
    form.toggle();
};

export const makeActive = ({ form, pageItem, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    form.setConfig({
        "title": "Make Signatory Active",
        "type": "update",
        "location": signatoryPath(variant, pageItem.ID),
        "fields": [],
        preSubmitHook() {
            return {
                "Cancelled": false,
                "UserSignOff": false,
                "UserSignOffDate": null,
                "UserSignOffBy": null
            }
        }
    });
    form.toggle();
};

export const addNote = ({ form, pageItem, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    form.setConfig({
        "title": "Add Note",
        "type": "push",
        "location": `${signatoryPath(variant, pageItem.ID)}/Notes`,
        "fields": [{
            "label": "Title",
            "name": "Title",
            "type": "text",
            "divClass": "md:col-span-3"
        }, {
            "label": "Note",
            "name": "Note",
            "type": "richtext",
            "divClass": "md:col-span-3"
        }],
        preSubmitHook() {
            return {
                "Date": (new Date()).toISOString(),
                "HoDApproval": false,
                "HOAApproval": false,
            }
        }
    });
    form.toggle();
};

export const updateNote = ({ form, rowItem, pageItem, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    form.setValues({
        "Note": rowItem.Note,
        "Title": rowItem.Title,
        "Date": rowItem.Date,
    });

    form.setConfig({
        "title": "Edit Note",
        "type": "update",
        "location": `${signatoryPath(variant, pageItem.ID)}/Notes/${rowItem.ID}`,
        "fields": [{
            "label": "Title",
            "name": "Title",
            "type": "text",
            "divClass": "md:col-span-3"
        }, {
            "label": "Date",
            "name": "Date",
            "type": "date"
        }, {
            "label": "Note",
            "name": "Note",
            "type": "richtext",
            "divClass": "md:col-span-3"
        }],
        preSubmitHook() {
            return {
                "HoDApproval": false,
                "HOAApproval": false,
            }
        }
    });
    form.toggle();
};

export const hodApproveNote = ({ form, rowItem, pageItem, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    form.setConfig({
        "title": "HOD Approve Note",
        "type": "update",
        "location": `${signatoryPath(variant, pageItem.ID)}/Notes/${rowItem.ID}`,
        "fields": [],
        preSubmitHook() {
            return {
                "HoDApproval": true,
                "HoDApprovalDate": (new Date()).toISOString()   
            }
        }
    });
    form.toggle();
};

export const hoaApproveNote = ({ form, rowItem, pageItem, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    form.setConfig({
        "title": "HOA Approve Note",
        "type": "update",
        "location": `${signatoryPath(variant, pageItem.ID)}/Notes/${rowItem.ID}`,
        "fields": [],
        preSubmitHook() {
            return {
                "HOAApproval": true,
                "HOAApprovalDate": (new Date()).toISOString()
            }
        }
    });
    form.toggle();
};

export const viewNote = ({ form, rowItem, pageItem, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    form.setValues({
        "Note": rowItem.Note,
        "Title": rowItem.Title,
    });

    form.setConfig({
        "title": " Note",
        "type": "view",
        "location": `${signatoryPath(variant, pageItem.ID)}/Notes/${rowItem.ID}`,
        "submitButtonLabel": "Close",
        "submitButtonClass": "primary",
        "fields": [{
            "label": "Title",
            value() {
                return rowItem.Title
            },
            "type": "value",
            "divClass": "md:col-span-3"
        }, {
            "label": "Note",
            value() {
                return rowItem.Note
            },
            "type": "value",
            "grow": true,
            "divClass": "md:col-span-3"
        }]
    });
    form.toggle();
};

export const updateAdditionalLimitations = ({ form, pageItem, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    form.setValues({
        "Limitations": pageItem.Limitations
    });

    form.setConfig({
        "title": "Edit Additional Limitations",
        "type": "update",
        "location": signatoryPath(variant, pageItem.ID),
        "fields": [{
            "label": "Additional Limitations",
            "name": "Limitations",
            "type": "richtext",
            "divClass": "md:col-span-3"
        }],
        postSubmitHook({ db }) {
            db.update(signatoryPath(variant, pageItem.ID), {
                UserSignOff: false,
                UserSignOffDate: null,
                UserSignOffBy: null
            });
        }
    });
    form.toggle();
};

export const addFormFourFile = type => ({ form, pageItem, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    form.setConfig({
        "title": "Add Form Four File",
        "type": "push",
        "location": `${signatoryPath(variant, pageItem.ID)}/Files`,
        "fields": [{
            "label": "File",
            "name": "File",
            "type": "file",
            "accept": ".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
        }],
        async postSubmitHook({ db, typeRef, files }) {
            for (const file of files) {
                await db.upload(`people/${pageItem.ID}`, file[0]);

                await db.set(`${signatoryPath(variant, pageItem.ID)}/Files/${typeRef.key}/File`, file[0].name);

                await db.set(`${signatoryPath(variant, pageItem.ID)}/FormFourFiles/${type}/`, file[0].name);
            }
        }
    });
    form.toggle();
};

export const linkFormFourFile = type => ({ form, pageItem, store, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    form.setConfig({
        "title": "Link File",
        "type": "update",
        "location": `${signatoryPath(variant, pageItem.ID)}/FormFourFiles/`,
        "fields": [{
            "label": "File",
            "name": type,
            "type": "dropdown",
            "dropdownData": Object.values(pageItem.Files).map(
                i => ({ label: i.File, id: i.File })
            )
        }]
    });
    form.getDropdownData();
    form.toggle();
};

export const reviseSignatoryMatrix = async ({ form, store, db, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    const signatoriesDataPath = signatoriesPath(variant);
    const revisionNumberKey = revisionNumberPath(variant);
    const revisionSnapshotsCollection = revisionSnapshotsPath(variant);
    const revisionIdKey = revisionIdPath(variant);
    const revisionRefsCollection = revisionRefsPath(variant);
    
    await db.store(revisionNumberKey, store);
    let changesMade = "";    
    const nextRev = next(store.state[revisionNumberKey]);
    if (nextRev === 1) {
        changesMade = "Initial Revision - Signatory Matrix moved from Handbook 2 Rev 43";
    } else {
        const revisionStateKey = variant.key === "military" ? "currentMilitarySignatoryMatrixRevision" : "currentSignatoryMatrixRevision";
        changesMade = changeIdentifier(store?.state?.[revisionStateKey]?.Signatories, store?.state[signatoriesDataPath]);
    }

    form.setConfig({
        "title": "Revise Signatory Matrix",
        "description": `Revise to Revision ${nextRev}`,
        "type": "push",
        "location": revisionSnapshotsCollection,
        "fields": [{
            "label": "Changes Made",
            "type": "value",
            "grow": true,
            "divClass": "md:col-span-3",
            "value":() => changesMade
        }],
        preSubmitHook() {
            return {
                "Signatories": store.state[signatoriesDataPath],
                "Revision": nextRev,
                "Date": (new Date()).toISOString(),
                "By": store.getters.userID,
                "ChangesMade": changesMade
            }
        },
        postSubmitHook({ typeRef }) {
            db.set(revisionNumberKey, next(store.state[revisionNumberKey]) || "A");
            db.set(revisionIdKey, typeRef.key);
            db.set(`${revisionRefsCollection}/${typeRef.key}`, `${nextRev} - ${new Date().toLocaleDateString()}`);

            // Reset user sign-off flags on the live data for the next change cycle.
            // The snapshot just taken has captured the sign-off states at the time of this revision.
            const liveSignatories = store.state[signatoriesDataPath] || {};
            Object.keys(liveSignatories).forEach((id) => {
                db.update(`${signatoriesDataPath}/${id}`, {
                    UserSignOff: false,
                    UserSignOffDate: null,
                    UserSignOffBy: null
                });
            });
        }
    });
    form.toggle();
};

export const reviewAnswer = locationStart => ({ form, rowItem, pageItem, store, db }) => {
    const question = store.state["Matrices/SignatoryMatrix/InterviewQuestions"][rowItem.ID];

    let fields = [];

    if (question.Answer) fields.push({
        "label": "Answer",
        "name": "Answer",
        "type": "value",
        value() {
            return (rowItem.Answer == true || rowItem.Answer == "true") ? "Yes" : "No"
        }
    })

    if (question?.SupportingInformation) fields.push({
        "label": "Supporting Information",
        "name": "SupportingInformation",
        "type": "value",
        "grow": true,
        "divClass": "md:col-span-3",
        value() {
            return rowItem.SupportingInformation
        }
    })
    form.setValues({
        "Points": rowItem.Points || 0
    })
    form.setConfig({
        "title": "Review Answer",
        "description": rowItem.Question,
        "type": "update",
        "location": `${locationStart}/Signatories/${pageItem.ID}/InterviewQuestions/${rowItem.ID}`,
        "fields": [...fields, {
            "break": true,
            "divClass": "md:col-span-3",
        }, {
            "label": `Award Points (Max: ${question?.MaxPoints || 1})`,
            "type": "number",
            "name": "Points",
            "step": "1",
            "min": 0,
            "max": question?.MaxPoints || 1
        }],
        preSubmitHook({ data }) {
            return {
                "Points": data.Points,
                "Date": {
                    "Reviewed": (new Date()).toISOString()
                },
                "ReviewedBy": store.getters.userID,
                "Answer": rowItem?.Answer || "",
                "SupportingInformation": rowItem?.SupportingInformation || ""
            }
        }
    });
    form.toggle();
};

export const answerQuestion = locationStart => ({ form, rowItem, pageItem, store, db }) => {

    form.setValues(rowItem);

    const question = store.state["Matrices/SignatoryMatrix/InterviewQuestions"][rowItem.ID];

    let fields = [];

    if (question.Answer) fields.push({
        "label": "Answer",
        "name": "Answer",
        "type": "dropdown",
        "dropdownData": [{
            "label": "Yes",
            "id": true
        }, {
            "label": "No",
            "id": false
        }]
    })

    if (question.SupportingInformation) fields.push({
        "label": "Supporting Information",
        "name": "SupportingInformation",
        "type": "richtext",
        "divClass": "md:col-span-3"
    })

    form.setConfig({
        "title": "Answer Answer",
        "description": rowItem.Question,
        "type": "update",
        "location": `${locationStart}/Signatories/${pageItem.ID}/InterviewQuestions/${rowItem.ID}`,
        "fields": fields
    });
    form.getDropdownData();
    form.toggle();
};

export const approveSignatoryApplication = ({ form, pageItem, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    form.setConfig({
        "title": "Approve Signatory Application",
        "type": "function",
        "functionName": "approveSignatoryApplication",
        "description": `Are you sure you want to approve ${pageItem.FirstName} ${pageItem.LastName}?`,
        "fields": [{
            "name": "csr",
            "type": "text",
            "label": "TSR Number",
            "required": true
        }, {
            "name": "confirmed",
            "type": "checkbox",
            "label": "I confirm",
            "required": true
        }],
        preSubmitHook({ data }) {
            if (data.confirmed !== true && data.confirmed !== "true") {
                throw new Error("You must confirm to approve this application.");
            }
            return {
                databaseURL: appConfig.firebaseConfig.databaseURL,
                variant: variant.key,
                applicationID: pageItem.ID,
                expectedAccountID: pageItem.AccountID,
                csr: data.csr,
                confirmed: true
            };
        }
    });
    form.toggle();
};

export const addTester = ({ db, store, form, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    form.setConfig({
        "title": "Add Tester",
        "submitButtonLabel": "Save",
        "type": "push",
        "location": testersPath(variant),
        "fields": [{
            "label": "Full Name",
            "name": "Name",
            "type": "text"
        }, {
            "label": "Types",
            "name": "Types",
            "type": "dropdown",
            "multiple": true,
            "dataLocation": "Matrices/SignatoryMatrix/TesterTypes",
            "labelKey": "Name",
            "idKey": "Name",
        }],
        preSubmitHook({ data }) {
            return {
                "Types": Array.isArray(data.Types) ? Object.values(data.Types).join(",") : data.Types
            }
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const editTester = ({ form, item, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    form.setValues({
        ...item,
        "Types": item?.Types?.split(", ")
    });
    form.setConfig({
        "title": "Edit Tester",
        "submitButtonLabel": "Save",
        "type": "update",
        "location": testerPath(variant, item.ID),
        "fields": [{
            "label": "Full Name",
            "name": "Name",
            "type": "text"
        }, {
            "label": "Types",
            "name": "Types",
            "type": "dropdown",
            "multiple": true,
            "dataLocation": "Matrices/SignatoryMatrix/TesterTypes",
            "labelKey": "Name",
            "idKey": "Name",
        }],
        preSubmitHook({ data }) {
            return {
                "Types": Array.isArray(data.Types) ? Object.values(data.Types).join(", ") : data.Types
            }
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const addFileToTester = ({ form, pageItem, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    form.setConfig({
        "title": "Add File",
        "submitButtonLabel": "Add",
        "type": "push",
        "location": `${testerPath(variant, pageItem.ID)}/Files`,
        "fields": [{
            "label": "File",
            "name": "File",
            "type": "file"
        }],
        preSubmitHook({ files }) {
            return {
                "Date": (new Date()).toISOString(),
                "File": files[0][0].name
            };
        },
        async postSubmitHook({ db, typeRef, files }) {
            for (const file of files) {
                await db.upload(`${testerPath(variant, pageItem.ID)}/Files/${typeRef.key}`, file[0]);
            }
        }
    });
    form.toggle();
}

export const downloadTesterFile = async ({ rowItem, pageItem, db, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    const url = await db.download(`${testerPath(variant, pageItem.ID)}/Files/${rowItem.ID}/${rowItem.File}`);
    window.open(url, "_blank");
};


const dropdownData = [{
    "id": "Design",
    "label": "Design"
}, {
    "id": "Certification",
    "label": "Certification"
}, {
    "id": "Project Management",
    "label": "Project Management"
}, {
    "id": "Design certification",
    "label": "Design certification"
}, {
    "id": "Other",
    "label": "Other"
}];

const fields = [{
    "label": "Date Start",
    "name": "Date[Start]",
    "type": "date"
}, {
    "label": "Date Completed",
    "name": "Date[Complete]",
    "type": "date"
}, {
    "label": "Type",
    "name": "Type",
    "type": "dropdown",
    "dropdownData": dropdownData
}, {
    "type": "text",
    "label": "Approved By",
    "name": "ApprovedBy"
}, {
    "label": "Training / Achievement / Experience",
    "name": "Training",
    "type": "text",
    "divClass": "md:col-span-2"
}, {
    "label": "Outcomes / Comments",
    "name": "Outcomes",
    "type": "text",
    "divClass": "md:col-span-3"
}, {
    "label": "Certificate / Proof",
    "name": "Certificate",
    "type": "file",
    "required": false,
    "divClass": "md:col-span-2"
}];

export const addRecord = ({ form, router, store, db }) => {
    const date = (new Date()).toISOString();
    form.setConfig({
        "title": "Add New Training Record",
        "type": "push",
        "description": `Userdata/${router?.currentRoute?.value?.params.id}/Training/Records`,
        "location": `Userdata/${router?.currentRoute?.value?.params.id}/Training/Records`,
        "fields": fields,
        async postSubmitHook({ files, typeRef }) {
            for (const file of files) {
                if (!file[0]) continue;
                await db.upload(`StaffTrainingRecords/${router?.currentRoute?.value?.params.id}/${typeRef.key}`, file[0]);
                await db.update(`Userdata/${router?.currentRoute?.value?.params.id}/Training/Records/${typeRef.key}`, {
                    "Certificate": file[0].name,
                    "AddedBy": store.getters.userID,
                })
            };
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const removeRecord = ({ form, pageItem, rowItem }) => {
    form.setConfig({
        "title": "Remove Training Record",
        "type": "delete",
        "description": "Are you sure you want to remove this record?",
        "location": `Userdata/${pageItem.Basic.key}/Training/Records/${rowItem.ID}`,
        "fields": []
    });

    form.toggle();
};

export const updateRecord = ({ form, pageItem, rowItem, store, db, router }) => {
    const date = (new Date()).toISOString();

    form.setValues({
        ...rowItem,
        "Date[Start]": rowItem?.Date?.Start,
        "Date[Complete]": rowItem?.Date?.Complete
    });

    form.setConfig({
        "title": "Revise Handbook",
        "location": `Userdata/${router?.currentRoute?.params.id}/Training/Records/${rowItem.ID}`,
        "type": "update",
        "fields": fields,

        async postSubmitHook({ files }) {
            for (const file of files) {

                if (!file[0]) continue;
                await db.upload(`StaffTrainingRecords/${router?.currentRoute?.params.id}/${rowItem.ID}`, file[0]);
                await db.update(`Userdata/${router?.currentRoute?.params.id}/Training/Records/${rowItem.ID}`, {
                    "Certificate": file[0].name,
                    "AddedBy": store.getters.userID,
                })
            };
        }
    });
    form.getDropdownData();

    form.toggle();
};

export const downloadCertificate = ({ pageItem, rowItem, db }) => {
    try {

        let rev = rowItem.Revision;
        // let url = await db.download()
        window.open(url, "__blank")
    } catch (err) {
        toast.error("Archive file found");
    }
}


export const updateAdditionalRoles = ({ form, pageItem, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    form.setValues({
        "Roles": pageItem.Roles
    });

    form.setConfig({
        "title": "Edit Additional Roles",
        "type": "update",
        "location": signatoryPath(variant, pageItem.ID),
        "fields": [{
            "label": "Additional Roles",
            "name": "Roles",
            "type": "richtext",
            "divClass": "md:col-span-3"
        }],
        postSubmitHook({ db }) {
            db.update(signatoryPath(variant, pageItem.ID), {
                UserSignOff: false,
                UserSignOffDate: null,
                UserSignOffBy: null
            });
        }
    });
    form.toggle();
}

export const addSampleSignature = ({ form, pageItem, store  }) => {
    
    form.setConfig({
        "title": "Upload Sample Signature",
        "type": "update",
        "location": `Userdata/${pageItem.ID}/Signature`,
        "fields": [{
            "label": "Signature",
            "name": "file",
            "type": "file",
            "accept": "image/png,image/jpeg,.svg"
        }],
        preSubmitHook({ files }) {
            if (!files[0]?.[0]) {
                throw new Error("Please select a file");
            }

            return {
                file: files[0][0].name
            }
        },
        async postSubmitHook({ files, db }) {
            
            if (!files[0]?.[0]) throw new Error("Please select a file");
            await db.upload(`Userdata/${pageItem.ID}/Signature`, files[0][0]);
        }
    });
    form.toggle();
};

export const userSignOffChanges = ({ form, pageItem, store, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    form.setConfig({
        "title": "Sign Off on Changes to Your Entry",
        "description": `Confirm that you have reviewed the changes made to your signatory matrix entry for ${pageItem.FirstName} ${pageItem.LastName}. Checking the box and submitting will record your sign-off. This is required before HoD/HoA can accept changes and issue the next revision.`,
        "type": "update",
        "submitButtonLabel": "Sign Off",
        "location": signatoryPath(variant, pageItem.ID),
        "fields": [{
            "name": "UserConfirmed",
            "type": "checkbox",
            "label": "I have reviewed and sign off on the changes to my entry",
            "required": true
        }],
        preSubmitHook() {
            return {
                "UserSignOff": true,
                "UserSignOffDate": (new Date()).toISOString(),
                "UserSignOffBy": store.getters.userID
            };
        }
    });
    form.toggle();
};

export const downloadRevisionHistory = async ({ db, store, meta }) => {
    const variant = meta?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT;
    const revisionSnapshotsCollection = revisionSnapshotsPath(variant);
    
    const htmlToPdfmake = (await import("html-to-pdfmake")).default;
    const { standardDateLocal } = await import("../../../utils");
    const appConfig = JSON.parse(import.meta.env.VITE_APP_CONFIG);

    // Ensure changeIdentifier dependencies are loaded
    await db.watch("Matrices/SignatoryMatrix/Panels", store);
    await db.watch("Matrices/SignatoryMatrix/ApprovedFunctions", store);

    // Fetch all revisions
    const revisionsObj = await db.once(revisionSnapshotsCollection);
    if (!revisionsObj) return;

    // Sort revisions by date ascending
    const revisions = Object.values(revisionsObj).sort((a, b) => {
        return new Date(a.Date) - new Date(b.Date);
    });

    if (revisions.length < 2) return;

    const docDef = {
        "content": [],
        "pageOrientation": "landscape",
        "header": {
            "text": "Signatory Matrix - Revision Change History",
            "margin": [42, 16, 0, 0],
            "bold": true
        },
        footer(currentPage, pageCount) {
            return {
                "text": `${currentPage} of ${pageCount}`,
                "margin": [42, 16, 0, 0],
                "bold": true
            };
        }
    };

    const con = docDef.content;

    for (let i = 0; i < revisions.length - 1; i++) {
        const older = revisions[i];
        const newer = revisions[i + 1];

        const changes = changeIdentifier(
            older.Signatories || {},
            newer.Signatories || {}
        );

        const newerDate = newer.Date
            ? new Date(newer.Date).toLocaleDateString("en-gb", standardDateLocal)
            : "No Date";

        // Section heading
        con.push({
            "text": `Rev ${older.Revision} to Rev ${newer.Revision}  —  ${newerDate}`,
            "bold": true,
            "fontSize": 12,
            "margin": [0, i > 0 ? 16 : 0, 0, 6],
            "fillColor": appConfig.primaryColour,
            "color": "#fff",
            "background": appConfig.primaryColour
        });

        // Changes content
        if (changes) {
            let parsed = htmlToPdfmake(changes);
            if (!Array.isArray(parsed)) parsed = [parsed];
            parsed.forEach(item => con.push(item));
        } else {
            con.push({ "text": "No changes detected.", "italics": true });
        }

        con.push({ "text": "\n" });
    }

    const { downloadPdf } = await import("../../../pdfDownloader.js");
    downloadPdf(docDef);
};
